import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Autocomplete, DateTime, Input } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';
import { storeData, updateData } from '~/store/modules/recalculaTurno/actions';

const MODO_TURNO = 'TURNO';
const MODO_DATA = 'DATA';

function normalizeValue(value) {
  if (value === undefined || value === null || `${value}`.trim() === '') {
    return null;
  }

  return `${value}`;
}

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);

  const { loading, find, params } = useSelector(
    (state) => state.recalculaTurno
  );

  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  const modoInicial = useMemo(() => {
    if (initials?.dataHoraprocessariniciohistoricorecalculaturno) {
      return MODO_DATA;
    }

    if (initials?.idturno) {
      return MODO_TURNO;
    }

    return null;
  }, [initials]);

  const postoAnteriorRef = useRef(initials?.idposto || null);

  const [modoRecalculo, setModoRecalculo] = useState(modoInicial);

  const [turnos, setTurnos] = useState([]);

  const [consultandoTurnos, setConsultandoTurnos] = useState(false);

  const [turnoSelecionado, setTurnoSelecionado] = useState(
    initials?.idturno || null
  );

  const [idpostoSelecionado, setIdpostoSelecionado] = useState(
    initials?.idposto || null
  );

  const [dataInicialSelecionada, setDataInicialSelecionada] = useState(
    initials?.datahorainicial || ''
  );

  const limparErros = useCallback(() => {
    reference.current?.setErrors({});
  }, [reference]);

  const limparSelecaoTurno = useCallback(() => {
    setTurnoSelecionado(null);

    reference.current?.setFieldValue('idturno', null);
  }, [reference]);

  const limparDataSelecaoTurno = useCallback(() => {
    setDataInicialSelecionada('');

    reference.current?.setFieldValue('datahorainicial', null);
  }, [reference]);

  const limparDataProcessamento = useCallback(() => {
    reference.current?.setFieldValue(
      'dataHoraprocessariniciohistoricorecalculaturno',
      null
    );
  }, [reference]);

  const limparCamposDependentesPosto = useCallback(() => {
    setModoRecalculo(null);
    setTurnos([]);

    limparSelecaoTurno();
    limparDataSelecaoTurno();
    limparDataProcessamento();
    limparErros();
  }, [
    limparDataProcessamento,
    limparDataSelecaoTurno,
    limparErros,
    limparSelecaoTurno,
  ]);

  const handleChangeModo = useCallback(
    (event) => {
      const novoModo = event.target.value;

      setModoRecalculo(novoModo);
      limparErros();

      if (novoModo === MODO_TURNO) {
        limparDataProcessamento();
        return;
      }

      setTurnos([]);
      limparSelecaoTurno();
      limparDataSelecaoTurno();
    },
    [
      limparDataProcessamento,
      limparDataSelecaoTurno,
      limparErros,
      limparSelecaoTurno,
    ]
  );

  const handleSelectPosto = useCallback(
    (value) => {
      const selectedValue =
        value && typeof value === 'object'
          ? value.idposto ?? null
          : value ?? null;

      const postoAnterior = normalizeValue(postoAnteriorRef.current);

      const novoPosto = normalizeValue(selectedValue);

      const postoFoiAlterado =
        postoAnterior !== null && postoAnterior !== novoPosto;

      setIdpostoSelecionado(selectedValue);
      postoAnteriorRef.current = selectedValue;

      if (postoFoiAlterado) {
        limparCamposDependentesPosto();
      }

      if (!selectedValue) {
        limparCamposDependentesPosto();
      }
    },
    [limparCamposDependentesPosto]
  );

  const handleSelectDataInicial = useCallback(
    (value) => {
      const dataAnterior = normalizeValue(dataInicialSelecionada);

      const novaData = normalizeValue(value);

      setDataInicialSelecionada(value || '');

      if (dataAnterior !== null && dataAnterior !== novaData) {
        setTurnos([]);
        limparSelecaoTurno();
      }
    },
    [dataInicialSelecionada, limparSelecaoTurno]
  );

  const handleSelectTurno = useCallback(
    (turnoId) => {
      setTurnoSelecionado(turnoId);

      reference.current?.setFieldValue('idturno', turnoId);

      limparErros();
    },
    [limparErros, reference]
  );

  const handleSubmit = useCallback(
    async (param) => {
      try {
        limparErros();

        if (!idpostoSelecionado) {
          reference.current?.setErrors({
            idposto: t('s.geral.validacao.campoobrigatorio'),
          });

          return;
        }

        if (!modoRecalculo) {
          toast.warning('Selecione uma forma de iniciar o recálculo.');

          return;
        }

        const payload = {
          ...param,
          idposto: param?.idposto || idpostoSelecionado,
        };

        if (modoRecalculo === MODO_TURNO) {
          payload.idturno = turnoSelecionado;

          payload.dataHoraprocessariniciohistoricorecalculaturno = null;
        } else {
          payload.idturno = null;
        }

        const schema = Yup.object().shape({
          idposto: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),

          idturno:
            modoRecalculo === MODO_TURNO
              ? Yup.number()
                  .transform((value, originalValue) => {
                    if (
                      originalValue === '' ||
                      originalValue === null ||
                      originalValue === undefined
                    ) {
                      return undefined;
                    }

                    return value;
                  })
                  .required('Selecione um turno.')
                  .typeError('Selecione um turno.')
                  .integer(t('s.geral.validacao.campoinvalido'))
                  .moreThan(
                    0,
                    t('s.geral.validacao.valorminimo').replace('%s', 1)
                  )
              : Yup.number().nullable(),

          dataHoraprocessariniciohistoricorecalculaturno:
            modoRecalculo === MODO_DATA
              ? Yup.string()
                  .nullable()
                  .transform((value, originalValue) => {
                    if (
                      originalValue === '' ||
                      originalValue === null ||
                      originalValue === undefined
                    ) {
                      return null;
                    }

                    return value;
                  })
                  .required('Informe a data de início do processamento.')
              : Yup.string().nullable(),
        });

        await schema.validate(payload, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          ...payload,
          idcliente: client,
        };

        delete objSend.datahorainicial;

        if (modoRecalculo === MODO_TURNO) {
          objSend.idturno = Number(turnoSelecionado);

          delete objSend.dataHoraprocessariniciohistoricorecalculaturno;
        } else {
          delete objSend.idturno;
        }

        if (create) {
          dispatch(storeData(objSend, params));
        } else {
          dispatch(updateData(objSend, params));
        }
      } catch (err) {
        if (!(err instanceof Yup.ValidationError)) {
          return;
        }

        const validationErrors = {};

        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current?.setErrors(validationErrors);

        const firstError = err.inner?.[0]?.message;

        if (firstError) {
          toast.warning(firstError);
        }
      }
    },
    [
      client,
      create,
      dispatch,
      idpostoSelecionado,
      initials,
      limparErros,
      modoRecalculo,
      params,
      reference,
      turnoSelecionado,
    ]
  );

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials, reference]);

  useEffect(() => {
    setModoRecalculo(modoInicial);

    setTurnoSelecionado(initials?.idturno || null);

    setIdpostoSelecionado(initials?.idposto || null);

    setDataInicialSelecionada(initials?.datahorainicial || '');

    postoAnteriorRef.current = initials?.idposto || null;
  }, [
    initials?.idturno,
    initials?.idposto,
    initials?.datahorainicial,
    modoInicial,
  ]);

  useEffect(() => {
    let active = true;

    async function fetchTurnos() {
      if (modoRecalculo !== MODO_TURNO) {
        setTurnos([]);
        setConsultandoTurnos(false);
        return;
      }

      if (!idpostoSelecionado || !dataInicialSelecionada) {
        setTurnos([]);
        setConsultandoTurnos(false);
        return;
      }

      try {
        setConsultandoTurnos(true);

        const response = await api.get('/turnos/resumoturnonovo', {
          params: {
            idposto: idpostoSelecionado,
            idcliente: client,
            dataturno: dataInicialSelecionada,
            pesquisalista: true,
          },

          paramsSerializer: (param) => qs.stringify(param),
        });

        const turnosEncontrados = (response.data || []).map((item) => ({
          idturno: item.idturno,
          numeroturno: item.numeroturno,
          horainicioturno: item.horaturno,
        }));

        if (!active) {
          return;
        }

        setTurnos(turnosEncontrados);

        if (turnoSelecionado) {
          const turnoAindaExiste = turnosEncontrados.some(
            (turno) => String(turno.idturno) === String(turnoSelecionado)
          );

          if (!turnoAindaExiste) {
            limparSelecaoTurno();
          }
        }
      } catch (err) {
        if (!active) {
          return;
        }

        setTurnos([]);
        limparSelecaoTurno();

        if (Array.isArray(err)) {
          err.forEach((error) => toast.error(t(error)));
        } else {
          toast.error(t(err));
        }
      } finally {
        if (active) {
          setConsultandoTurnos(false);
        }
      }
    }

    fetchTurnos();

    return () => {
      active = false;
    };
  }, [client, dataInicialSelecionada, idpostoSelecionado, modoRecalculo]);

  const turnoSelecionadoCompleto = turnos.find(
    (turno) => String(turno.idturno) === String(turnoSelecionado)
  );

  let conteudoTabelaTurnos;

  if (consultandoTurnos) {
    conteudoTabelaTurnos = (
      <TableRow>
        <TableCell colSpan={3} align="center">
          <div
            style={{
              minHeight: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner loading />
          </div>
        </TableCell>
      </TableRow>
    );
  } else if (turnos.length === 0) {
    conteudoTabelaTurnos = (
      <TableRow>
        <TableCell colSpan={3} align="center">
          {dataInicialSelecionada
            ? 'Nenhum turno encontrado para a data selecionada.'
            : 'Informe uma data para localizar os turnos.'}
        </TableCell>
      </TableRow>
    );
  } else {
    conteudoTabelaTurnos = turnos.map((turno) => (
      <TableRow
        hover
        key={turno.idturno}
        selected={String(turnoSelecionado) === String(turno.idturno)}
        onClick={() => handleSelectTurno(turno.idturno)}
        style={{ cursor: 'pointer' }}
      >
        <TableCell padding="checkbox">
          <Radio
            color="primary"
            checked={String(turnoSelecionado) === String(turno.idturno)}
            onChange={() => handleSelectTurno(turno.idturno)}
            value={turno.idturno}
            inputProps={{
              'aria-label': `Selecionar turno ${turno.numeroturno}`,
            }}
          />
        </TableCell>

        <TableCell>{turno.numeroturno}</TableCell>

        <TableCell>{turno.horainicioturno}</TableCell>
      </TableRow>
    ));
  }

  return loading ? (
    <div
      style={{
        height: '14rem',
        width: '50rem',
      }}
    >
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <Autocomplete
        name="idposto"
        label="codigonomeposto"
        url="postoscomboios"
        unique="idposto"
        title={t('s.geral.postocomboio')}
        getValue={handleSelectPosto}
      />

      <Input name="idturno" type="hidden" />

      {!idpostoSelecionado && (
        <Paper
          variant="outlined"
          style={{
            padding: 12,
            marginTop: 16,
            backgroundColor: '#f5f5f5',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Selecione o Posto/Comboio para visualizar as opções de recálculo.
          </Typography>
        </Paper>
      )}

      {idpostoSelecionado && (
        <>
          <div style={{ marginTop: 20 }}>
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Escolha como deseja iniciar o recálculo
            </Typography>

            <RadioGroup value={modoRecalculo || ''} onChange={handleChangeModo}>
              <FormControlLabel
                value={MODO_TURNO}
                control={<Radio color="primary" />}
                label="Recalcular por turno"
              />

              <FormControlLabel
                value={MODO_DATA}
                control={<Radio color="primary" />}
                label="Recalcular a partir de uma data"
              />
            </RadioGroup>
          </div>

          {modoRecalculo === MODO_TURNO && (
            <Paper
              variant="outlined"
              style={{
                padding: 16,
                marginTop: 12,
                borderColor: '#3f51b5',
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  marginBottom: 16,
                }}
              >
                Informe uma data para localizar os turnos do Posto/Comboio
                selecionado.
              </Typography>

              <DateTime
                name="datahorainicial"
                label="Data para localizar os turnos"
                getValue={handleSelectDataInicial}
              />

              <Typography
                variant="subtitle2"
                style={{
                  marginTop: 18,
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                Turnos encontrados
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="turnos">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />

                      <TableCell>{t('s.menu.operacao.turno')}</TableCell>

                      <TableCell>
                        {t('s.relatorio.vazaonaoautorizadalistagem.inicio')}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>{conteudoTabelaTurnos}</TableBody>
                </Table>
              </TableContainer>

              {turnoSelecionadoCompleto && (
                <Paper
                  variant="outlined"
                  style={{
                    padding: 12,
                    marginTop: 12,
                    backgroundColor: '#f5f8ff',
                    borderColor: '#c5d4f7',
                  }}
                >
                  <Typography variant="body2">
                    O recálculo será realizado a partir das{' '}
                    <strong>{turnoSelecionadoCompleto.horainicioturno}</strong>,
                    referente ao turno{' '}
                    <strong>{turnoSelecionadoCompleto.numeroturno}</strong>.
                  </Typography>
                </Paper>
              )}
            </Paper>
          )}

          {modoRecalculo === MODO_DATA && (
            <Paper
              variant="outlined"
              style={{
                padding: 16,
                marginTop: 12,
                borderColor: '#3f51b5',
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  marginBottom: 16,
                }}
              >
                Informe a data e a hora a partir das quais o recálculo será
                realizado.
              </Typography>

              <DateTime
                time
                nullable
                name="dataHoraprocessariniciohistoricorecalculaturno"
                label={t(
                  's.operacao.historicorecalculaturno.dataHoraprocessariniciohistoricorecalculaturno'
                )}
              />

              <Paper
                variant="outlined"
                style={{
                  padding: 12,
                  marginTop: 12,
                  backgroundColor: '#f5f8ff',
                  borderColor: '#c5d4f7',
                }}
              >
                <Typography variant="body2">
                  O recálculo será realizado a partir da data e hora informadas.
                </Typography>
              </Paper>
            </Paper>
          )}

          {modoRecalculo && (
            <Paper
              variant="outlined"
              style={{
                padding: 12,
                marginTop: 16,
                backgroundColor: '#fff8e5',
                borderColor: '#f1d28a',
              }}
            >
              <Typography variant="body2">
                <strong>Importante:</strong> as opções são exclusivas. Escolha
                somente uma forma de iniciar o recálculo.
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.shape({
    current: PropTypes.shape({
      setData: PropTypes.func,
      setErrors: PropTypes.func,
      setFieldValue: PropTypes.func,
    }),
  }).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.shape({}),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
